/**
 * Generic results card -- renders module-specific result content via children
 * and exposes the standalone-mode download buttons (JSON / report / copy).
 *
 * Downloads are produced client-side from a Blob so they ship today even
 * before any native PDF/CSV endpoints exist. Native export endpoints can
 * replace `data` -> blob with a server fetch without changing the UI.
 */
import React, { useState } from 'react';
import { Icon } from './WorkflowIcons';

function safeFilename(s) {
  return String(s || 'result').replace(/[^a-z0-9_-]+/gi, '_').slice(0, 64) || 'result';
}

function downloadBlob(filename, mime, content) {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export default function ResultsPanel({
  title = 'Results',
  data = null,
  downloadName = 'result',
  textReport = null,
  csv = null,
  printableRef = null,
  extraActions = null,
  children,
}) {
  const [copied, setCopied] = useState(false);

  const onCopy = async () => {
    try {
      const txt = textReport || (data ? JSON.stringify(data, null, 2) : '');
      if (!txt) return;
      await navigator.clipboard.writeText(txt);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard blocked -- fall back to download */
      const txt = textReport || (data ? JSON.stringify(data, null, 2) : '');
      downloadBlob(`${safeFilename(downloadName)}.txt`, 'text/plain', txt);
    }
  };

  const onDownloadJson = () => {
    if (!data) return;
    downloadBlob(`${safeFilename(downloadName)}.json`, 'application/json', JSON.stringify(data, null, 2));
  };

  const onDownloadText = () => {
    if (!textReport) return;
    downloadBlob(`${safeFilename(downloadName)}.txt`, 'text/plain', textReport);
  };

  const onDownloadCsv = () => {
    if (!csv) return;
    downloadBlob(`${safeFilename(downloadName)}.csv`, 'text/csv', csv);
  };

  const onPrint = () => {
    if (printableRef?.current) {
      const w = window.open('', '_blank');
      if (!w) return;
      w.document.write(`<!doctype html><html><head><title>${downloadName}</title>
        <style>body{font:14px -apple-system,Segoe UI,sans-serif;padding:24px;color:#0f172a;}
        pre{white-space:pre-wrap;font:12px ui-monospace,SFMono-Regular,Menlo,monospace;}
        h1,h2,h3{color:#0f172a;}</style></head><body>${printableRef.current.innerHTML}</body></html>`);
      w.document.close();
      w.focus();
      setTimeout(() => w.print(), 200);
    } else {
      window.print();
    }
  };

  return (
    <section className="wf-step-card">
      <header className="wf-step-card__head">
        <h2 className="wf-step-card__title">
          <span className="wf-step-card__num">4</span>
          {title}
        </h2>
        <div className="wf-actions">
          {extraActions}
          {data && (
            <button type="button" className="wf-btn wf-btn--ghost" onClick={onDownloadJson} title="Download JSON">
              <Icon.download /> JSON
            </button>
          )}
          {textReport && (
            <button type="button" className="wf-btn wf-btn--ghost" onClick={onDownloadText} title="Download text report">
              <Icon.download /> Report
            </button>
          )}
          {csv && (
            <button type="button" className="wf-btn wf-btn--ghost" onClick={onDownloadCsv} title="Download CSV">
              <Icon.download /> CSV
            </button>
          )}
          {printableRef && (
            <button type="button" className="wf-btn wf-btn--ghost" onClick={onPrint} title="Print to PDF">
              <Icon.download /> PDF
            </button>
          )}
          {(data || textReport) && (
            <button type="button" className="wf-btn wf-btn--ghost" onClick={onCopy}>
              <Icon.copy /> {copied ? 'Copied!' : 'Copy'}
            </button>
          )}
        </div>
      </header>
      <div className="wf-results">{children}</div>
    </section>
  );
}
